import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  evaluateBackfillForUser,
  applyBackfillForUser,
} from "../../../lib/data/availability";
import { formatZonedTime } from "../../../lib/time";

const PAGE_SIZE = 25;

export function AvailabilityBackfillDialog({
  open,
  onOpenChange,
  onConfirmed,
  displayTimeZone,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [polls, setPolls] = useState([]);
  // checked[schedulerId][slotId] = boolean
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(0);
    evaluateBackfillForUser()
      .then(({ data }) => {
        if (cancelled) return;
        const ps = data?.polls || [];
        setPolls(ps);
        const initial = {};
        for (const p of ps) {
          initial[p.schedulerId] = {};
          for (const it of p.items) initial[p.schedulerId][it.slotId] = true;
        }
        setChecked(initial);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Couldn't load preview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Flat list of all items across all polls
  const flatItems = useMemo(
    () =>
      polls.flatMap((p) =>
        p.items.map((it) => ({
          schedulerId: p.schedulerId,
          schedulerTitle: p.schedulerTitle,
          item: it,
        }))
      ),
    [polls]
  );

  const paginated = flatItems.length > 50;
  const totalPages = Math.max(1, Math.ceil(flatItems.length / PAGE_SIZE));

  // Slice to the current page (or all items if not paginated)
  const visibleSlice = paginated
    ? flatItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : flatItems;

  // Re-group the visible slice by poll for rendering
  const visiblePollGroups = useMemo(() => {
    const groups = new Map();
    for (const entry of visibleSlice) {
      if (!groups.has(entry.schedulerId)) {
        groups.set(entry.schedulerId, {
          schedulerId: entry.schedulerId,
          schedulerTitle: entry.schedulerTitle,
          items: [],
        });
      }
      groups.get(entry.schedulerId).items.push(entry.item);
    }
    return Array.from(groups.values());
  }, [visibleSlice]);

  const totalChecked = Object.values(checked).reduce(
    (acc, m) => acc + Object.values(m).filter(Boolean).length,
    0
  );

  function toggleSlot(schedulerId, slotId) {
    setChecked((prev) => ({
      ...prev,
      [schedulerId]: {
        ...(prev[schedulerId] || {}),
        [slotId]: !prev[schedulerId]?.[slotId],
      },
    }));
  }

  function togglePoll(schedulerId, value) {
    setChecked((prev) => {
      const next = { ...prev, [schedulerId]: { ...(prev[schedulerId] || {}) } };
      const poll = polls.find((p) => p.schedulerId === schedulerId);
      if (poll) {
        for (const it of poll.items) next[schedulerId][it.slotId] = value;
      }
      return next;
    });
  }

  async function handleApply() {
    setApplying(true);
    try {
      const acceptedSlotIdsByPoll = {};
      for (const [schedulerId, m] of Object.entries(checked)) {
        const ids = Object.entries(m)
          .filter(([, v]) => v)
          .map(([id]) => id);
        if (ids.length > 0) acceptedSlotIdsByPoll[schedulerId] = ids;
      }
      await applyBackfillForUser({ acceptedSlotIdsByPoll });
      onConfirmed();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Couldn't apply backfill");
    } finally {
      setApplying(false);
    }
  }

  function handleCancel() {
    // User still wants auto-vote enabled going forward; they just opted out
    // of pre-filling existing open polls. Surface that intent to the parent.
    onConfirmed();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Pre-fill your open polls?</DialogTitle>
          <DialogDescription>
            We'd auto-fill the following votes across your open polls based on
            your availability. Uncheck any you'd rather leave blank.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div role="status" className="py-6 text-center text-sm">
            Loading…
          </div>
        )}
        {!loading && error && (
          <p role="alert" className="py-2 text-sm text-red-600">
            {error}
          </p>
        )}
        {!loading && !error && polls.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500">
            No open polls would be affected. You're good to go.
          </p>
        )}
        {!loading && !error && polls.length > 0 && (
          <>
            <div className="max-h-96 space-y-4 overflow-y-auto">
              {visiblePollGroups.map((p) => (
                <section key={p.schedulerId}>
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-slate-700 dark:text-slate-200">
                      {p.schedulerTitle}
                    </h4>
                    <span className="flex gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => togglePoll(p.schedulerId, true)}
                        className="text-brand-primary hover:underline dark:text-blue-300"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePoll(p.schedulerId, false)}
                        className="text-slate-500 hover:underline"
                      >
                        Deselect all
                      </button>
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {p.items.map((it) => (
                      <li
                        key={it.slotId}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          aria-label={`Pre-fill slot ${it.slotId}`}
                          checked={!!checked[p.schedulerId]?.[it.slotId]}
                          onChange={() => toggleSlot(p.schedulerId, it.slotId)}
                        />
                        <span>
                          {it.start
                            ? formatZonedTime(
                                new Date(it.start),
                                displayTimeZone,
                                "MMM d h:mm a",
                                { showTimeZone: false }
                              )
                            : "(unknown)"}
                        </span>
                        <span
                          className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${chipClass(
                            it.proposedVote,
                            it.autoSource
                          )}`}
                        >
                          {chipLabel(it.proposedVote, it.autoSource)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {paginated && (
              <div className="flex items-center justify-center gap-3 pt-2 text-sm">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-lg border px-3 py-1 disabled:opacity-40"
                  aria-label="Prev"
                >
                  Prev
                </button>
                <span>
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-lg border px-3 py-1 disabled:opacity-40"
                  aria-label="Next"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        <DialogFooter className="sm:justify-between">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-xl border px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={applying || polls.length === 0}
            onClick={handleApply}
            className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary/90 disabled:opacity-50"
          >
            {applying
              ? "Applying…"
              : `Apply to ${totalChecked} slot${totalChecked === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function chipLabel(proposedVote, autoSource) {
  if (autoSource === "auto-conflict") return "NO";
  return proposedVote || "—";
}

function chipClass(proposedVote, autoSource) {
  if (autoSource === "auto-conflict") {
    return "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200";
  }
  if (proposedVote === "PREFERRED") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200";
  }
  return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200";
}

export default AvailabilityBackfillDialog;
